import {
  EvidenceSubmissionArtifactSnapshotSchema,
  isPeriodRollupArtifact,
  isSubmissionManifest,
  type EvidenceSubmissionDTO,
} from '@deepguide-ai/types'

/** Historical rollups remain readable, but are not supplemental evidence. */
export function partitionRollupEvidence<T extends { id: string; metadata?: unknown }>(
  files: readonly T[],
  rollupFileIds: ReadonlySet<string>,
): { current: T[]; historical: T[] } {
  const current: T[] = []
  const historical: T[] = []
  for (const file of files) {
    const isHistorical =
      isPeriodRollupArtifact(file.metadata) ||
      isSubmissionManifest(file.metadata) ||
      rollupFileIds.has(file.id)
    ;(isHistorical ? historical : current).push(file)
  }
  return { current, historical }
}

export type SubmissionArtifactSnapshot = {
  evidenceDocLinks: Array<{
    id: string
    documentType: string
    documentTitle: string
    documentId: string
    versionId?: string | null
    version?: number | null
    policySlug?: string | null
    viewUrl?: string | null
    downloadUrl?: string | null
    fileName?: string | null
    mimeType?: string | null
    isApproved?: boolean
    isProgramBackedDeliverable: boolean
  }>
  files?: Array<{
    id: string
    filename: string
    url?: string
    mimeType?: string | null
    size?: number | null
    metadata?: Record<string, unknown>
    uploadedAt?: string
  }>
}

export function readSubmissionArtifactSnapshot(
  value: unknown,
  fallbackFiles?: EvidenceSubmissionDTO['additionalFiles'],
): SubmissionArtifactSnapshot | null {
  const parsed = EvidenceSubmissionArtifactSnapshotSchema.safeParse(value)
  if (!parsed.success) {
    return fallbackFiles?.length
      ? {
          evidenceDocLinks: [],
          files: fallbackFiles.map((file) => ({
            ...file,
            uploadedAt: file.uploadedAt instanceof Date
              ? file.uploadedAt.toISOString()
              : String(file.uploadedAt),
          })),
        }
      : null
  }

  const evidenceDocLinks: SubmissionArtifactSnapshot['evidenceDocLinks'] = []
  const files: NonNullable<SubmissionArtifactSnapshot['files']> = []

  for (const artifact of parsed.data.artifacts) {
    if (artifact.kind === 'file' && typeof artifact.filename === 'string') {
      // The recorded manifest supersedes manifests inherited from older submissions.
      if (parsed.data.manifest && (artifact.filename === 'manifest.json' || isSubmissionManifest(artifact.metadata))) {
        if (artifact.url !== parsed.data.manifest.url || files.some(file => file.url === parsed.data.manifest!.url)) continue
      }
      files.push({
        ...(artifact.metadata ? { metadata: artifact.metadata } : {}),
        id:
          typeof artifact.id === 'string'
            ? artifact.id
            : `file:${artifact.filename}`,
        filename: artifact.filename,
        url: typeof artifact.url === 'string' ? artifact.url : undefined,
        mimeType:
          typeof artifact.mimeType === 'string' ? artifact.mimeType : null,
        size: typeof artifact.size === 'number' ? artifact.size : null,
        uploadedAt:
          typeof artifact.uploadedAt === 'string'
            ? artifact.uploadedAt
            : undefined,
      })
      continue
    }

    if (
      typeof artifact.programId === 'string' &&
      typeof artifact.documentTitle === 'string' &&
      typeof artifact.documentUrl === 'string'
    ) {
      evidenceDocLinks.push({
        id:
          typeof artifact.id === 'string'
            ? artifact.id
            : `${artifact.kind}:${artifact.programId}`,
        documentType: artifact.kind,
        documentTitle: artifact.documentTitle,
        documentId: artifact.programId,
        versionId: null,
        version: artifact.systemDescriptionVersion ?? null,
        downloadUrl: artifact.documentUrl,
        fileName: artifact.documentTitle,
        mimeType:
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        isProgramBackedDeliverable: true,
      })
      continue
    }

    const isPolicy =
      artifact.kind === 'policy' || artifact.kind === 'policy_link'
    const isVault = artifact.kind === 'vault'
    if (!isPolicy && !isVault) continue
    if (
      typeof artifact.documentId !== 'string' ||
      typeof artifact.documentTitle !== 'string'
    ) {
      continue
    }

    const versionId =
      typeof artifact.versionId === 'string'
        ? artifact.versionId
        : typeof artifact.policyVersionId === 'string'
          ? artifact.policyVersionId
          : null

    evidenceDocLinks.push({
      id:
        typeof artifact.id === 'string'
          ? artifact.id
          : `${isPolicy ? 'policy' : 'vault'}:${artifact.documentId}:${versionId ?? 'unversioned'}`,
      documentType:
        typeof artifact.documentType === 'string'
          ? artifact.documentType
          : isPolicy
            ? 'policy'
            : 'vault',
      documentTitle: artifact.documentTitle,
      documentId: artifact.documentId,
      versionId,
      version:
        typeof artifact.policyVersion === 'number'
          ? artifact.policyVersion
          : null,
      isProgramBackedDeliverable: false,
      ...(artifact.policySlug ? { policySlug: artifact.policySlug } : {}),
    })
  }

  for (const file of files) {
    const checked = fallbackFiles?.find(candidate => candidate.id === file.id && candidate.url === file.url)
    const availability = checked?.metadata?.storageAvailability
    if (availability === 'available' || availability === 'unavailable') {
      file.metadata = { ...file.metadata, storageAvailability: availability, storageCheckedAt: checked?.metadata?.storageCheckedAt }
    }
  }
  const manifest = parsed.data.manifest
  if (manifest && !files.some(file => file.url === manifest.url)) {
    files.push({
      id: `manifest:${manifest.sha256}`,
      filename: 'manifest.json',
      url: manifest.url,
      mimeType: 'application/json',
      uploadedAt: manifest.signedAt ?? undefined,
      metadata: { sourceType: 'submission_artifact', artifactKind: 'manifest', ...manifest },
    })
  }

  return { evidenceDocLinks, files }
}

export function readSubmittedPolicyVersion(value: unknown): {
  versionId: string | null
  version: number | null
} | null {
  const snapshot = readSubmissionArtifactSnapshot(value)
  const policy = snapshot?.evidenceDocLinks.find(
    (link) => link.documentType === 'policy'
  )
  return policy
    ? { versionId: policy.versionId ?? null, version: policy.version ?? null }
    : null
}

/** Restore display grouping only when the stored row still identifies the exact frozen object. */
export function restoreSnapshotFileMetadata(
  files: SubmissionArtifactSnapshot['files'],
  storedFiles: Array<{ id: string; url?: string | null; metadata?: unknown }> | undefined,
): NonNullable<SubmissionArtifactSnapshot['files']> {
  return (files ?? []).map(file => {
    if (file.metadata) return file
    const stored = storedFiles?.find(row => row.id === file.id && row.url === file.url && !!file.url)
    if (!stored?.metadata || typeof stored.metadata !== 'object' || Array.isArray(stored.metadata)) return file
    return { ...file, metadata: stored.metadata as Record<string, unknown> }
  })
}
